import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, X, Save, Sparkles } from 'lucide-react';
import { useProMaxModelById, createProMaxModel, updateProMaxModel } from '../hooks/useProMaxModels';
import { ProMaxModel, ProMaxInputField } from '../types/promax';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { parseAdminStaffSession } from '../utils/adminSession';
import { AdminPageToolbar } from '../components/admin/AdminLayout';

type OutputType = 'image' | 'video' | 'audio' | 'text' | 'json';
type Category = 'image' | 'video' | 'audio' | 'text';
type FieldType = 'text' | 'textarea' | 'number' | 'select' | 'boolean' | 'range' | 'file' | 'dropdown_with_image' | 'prompt_builder';

export const ProMaxModelFormPage = () => {
  const navigate = useNavigate();
  const { modelId } = useParams<{ modelId: string }>();
  const isEditMode = Boolean(modelId);
  const { model: existingModel, loading: loadingModel, error: loadModelError } = useProMaxModelById(modelId || '');

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [apiDocumentation, setApiDocumentation] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    model_id: '',
    app_id: '',
    name: '',
    description: '',
    fal_endpoint: '',
    category: 'image' as Category,
    subcategory: '',
    tags: '',
    icon_name: 'Zap',
    icon_color: 'text-brand',
    icon_bg: 'bg-brand/20',
    credit_cost: 1,
    estimated_time: '10-15 seconds',
    output_type: 'image' as OutputType,
    is_active: true,
    is_featured: false,
    coming_soon: false,
    fal_stream_node_id: '',
    fal_polling_node_name: '',
    is_long_running: false,
    polling_interval_seconds: 5,
    sort_order: 100,
    primary_output_key: 'images',
    secondary_output_keys: '',
    metadata_keys: '',
    error_key: 'error'
  });

  const [inputFields, setInputFields] = useState<ProMaxInputField[]>([]);

  useEffect(() => {
    const access = parseAdminStaffSession();
    if (!access) {
      navigate('/admin');
      return;
    }

    try {
      if (access?.role !== 'superadmin') {
        alert('Access denied. Only superadmins can manage ProMax models.');
        navigate('/admin');
      }
    } catch (err) {
      console.error('Error parsing admin access:', err);
      navigate('/admin');
    }
  }, [navigate]);

  useEffect(() => {
    if (existingModel) {
      setFormData({
        model_id: existingModel.model_id,
        app_id: existingModel.app_id ?? '',
        name: existingModel.name,
        description: existingModel.description,
        fal_endpoint: existingModel.fal_endpoint,
        category: existingModel.category,
        subcategory: existingModel.subcategory,
        tags: existingModel.tags.join(', '),
        icon_name: existingModel.icon_name,
        icon_color: existingModel.icon_color,
        icon_bg: existingModel.icon_bg,
        credit_cost: existingModel.credit_cost,
        estimated_time: existingModel.estimated_time,
        output_type: existingModel.output_type,
        is_active: existingModel.is_active,
        is_featured: existingModel.is_featured,
        coming_soon: existingModel.coming_soon,
        fal_stream_node_id: existingModel.fal_stream_node_id,
        fal_polling_node_name: existingModel.fal_polling_node_name,
        is_long_running: existingModel.is_long_running,
        polling_interval_seconds: existingModel.polling_interval_seconds,
        sort_order: existingModel.sort_order,
        primary_output_key: existingModel.output_mapping.primary_key,
        secondary_output_keys: existingModel.output_mapping.secondary_keys?.join(', ') || '',
        metadata_keys: existingModel.output_mapping.metadata_keys?.join(', ') || '',
        error_key: existingModel.output_mapping.error_key || 'error'
      });
      setInputFields(existingModel.input_schema);
    }
  }, [existingModel, navigate]);

  const goBack = () => {
    navigate('/admin/promax-models');
  };

  const handleAddField = () => {
    const newField: ProMaxInputField = {
      id: `field_${Date.now()}`,
      name: '',
      label: '',
      type: 'text',
      required: false
    };
    setInputFields([...inputFields, newField]);
  };

  const handleRemoveField = (index: number) => {
    setInputFields(inputFields.filter((_, i) => i !== index));
  };

  const handleFieldChange = (index: number, updates: Partial<ProMaxInputField>) => {
    const updated = [...inputFields];
    updated[index] = { ...updated[index], ...updates };
    setInputFields(updated);
  };

  const handleGenerateModel = async () => {
    if (!apiDocumentation.trim()) {
      setGenerateError('Please paste API documentation');
      return;
    }

    setIsGenerating(true);
    setGenerateError(null);

    try {
      const openaiKey = import.meta.env.VITE_OPENAI_KEY;
      if (!openaiKey) {
        throw new Error('OpenAI API key not configured');
      }

      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${openaiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            {
              role: 'system',
              content: 'You are an AI assistant that parses FAL API documentation and extracts structured model configuration. Return ONLY valid JSON without markdown formatting or code blocks.'
            },
            {
              role: 'user',
              content: `Parse the following FAL API documentation and extract all configuration details. Return a JSON object with this exact structure:

{
  "model_id": "extracted-model-id",
  "name": "Model Display Name",
  "description": "Brief description",
  "fal_endpoint": "fal-ai/model-endpoint",
  "category": "image|video|audio|text",
  "subcategory": "generation|enhancement|etc",
  "tags": ["tag1", "tag2"],
  "output_type": "image|video|audio|text|json",
  "primary_output_key": "images",
  "secondary_output_keys": ["key1", "key2"],
  "input_fields": [
    {
      "id": "field_123",
      "name": "prompt",
      "label": "Prompt",
      "type": "text|textarea|number|select|boolean|range|file",
      "required": true,
      "default_value": "",
      "min": 1,
      "max": 100,
      "step": 1,
      "options": ["option1", "option2"],
      "placeholder": "Enter value",
      "description": "Field description"
    }
  ]
}

API Documentation:
${apiDocumentation}

Important:
- Extract model_id from "Model ID" field
- Extract fal_endpoint from "Endpoint" field
- For each input parameter, create a field with appropriate type (text, number, select, boolean, range, etc.)
- Use "textarea" for long text fields like "prompt"
- Use "select" for enum/options fields
- Use "range" for number fields with min/max
- Use "boolean" for boolean fields
- Set required=true if marked as "required" in docs
- Extract default values, min, max, step, and options when available
- Return ONLY the JSON object, no other text`
            }
          ],
          temperature: 0.3
        })
      });

      if (!response.ok) {
        throw new Error(`OpenAI API error: ${response.statusText}`);
      }

      const data = await response.json();
      const content = data.choices[0].message.content.trim();

      // Remove markdown code blocks if present
      const jsonContent = content.replace(/^```json\n?/i, '').replace(/\n?```$/i, '').trim();
      const parsedConfig = JSON.parse(jsonContent);

      // Populate form data
      setFormData({
        ...formData,
        model_id: parsedConfig.model_id || '',
        name: parsedConfig.name || '',
        description: parsedConfig.description || '',
        fal_endpoint: parsedConfig.fal_endpoint || '',
        category: parsedConfig.category || 'image',
        subcategory: parsedConfig.subcategory || '',
        tags: Array.isArray(parsedConfig.tags) ? parsedConfig.tags.join(', ') : '',
        output_type: parsedConfig.output_type || 'image',
        primary_output_key: parsedConfig.primary_output_key || 'images',
        secondary_output_keys: Array.isArray(parsedConfig.secondary_output_keys)
          ? parsedConfig.secondary_output_keys.join(', ')
          : ''
      });

      // Populate input fields
      if (Array.isArray(parsedConfig.input_fields)) {
        setInputFields(parsedConfig.input_fields);
      }

      setShowGenerateModal(false);
      setApiDocumentation('');
      alert('Model configuration generated successfully! Review and adjust as needed.');
    } catch (err) {
      console.error('Error generating model:', err);
      setGenerateError(err instanceof Error ? err.message : 'Failed to generate model configuration');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    setError(null);
    setIsSaving(true);

    try {
      if (!formData.model_id || !formData.name || !formData.fal_endpoint) {
        throw new Error('Model ID, Name, and FAL Endpoint are required');
      }
      if (!formData.app_id?.trim()) {
        throw new Error('Hyper App ID is required (set in this form — same as your Hyper Connect app for this surface)');
      }

      const tagsArray = formData.tags.split(',').map(t => t.trim()).filter(Boolean);
      const secondaryKeys = formData.secondary_output_keys.split(',').map(t => t.trim()).filter(Boolean);
      const metadataKeys = formData.metadata_keys.split(',').map(t => t.trim()).filter(Boolean);

      const modelData: Partial<ProMaxModel> = {
        model_id: formData.model_id,
        app_id: formData.app_id.trim(),
        name: formData.name,
        description: formData.description,
        fal_endpoint: formData.fal_endpoint,
        input_schema: inputFields,
        output_mapping: {
          primary_key: formData.primary_output_key,
          secondary_keys: secondaryKeys.length > 0 ? secondaryKeys : undefined,
          metadata_keys: metadataKeys.length > 0 ? metadataKeys : undefined,
          error_key: formData.error_key
        },
        category: formData.category,
        subcategory: formData.subcategory,
        tags: tagsArray,
        icon_name: formData.icon_name,
        icon_color: formData.icon_color,
        icon_bg: formData.icon_bg,
        credit_cost: formData.credit_cost,
        estimated_time: formData.estimated_time,
        output_type: formData.output_type,
        is_active: formData.is_active,
        is_featured: formData.is_featured,
        coming_soon: formData.coming_soon,
        fal_stream_node_id: formData.fal_stream_node_id,
        fal_polling_node_name: formData.fal_polling_node_name,
        is_long_running: formData.is_long_running,
        polling_interval_seconds: formData.polling_interval_seconds,
        sort_order: formData.sort_order
      };

      const accessRecord = parseAdminStaffSession();
      if (!accessRecord) {
        throw new Error('No admin session found');
      }
      const { hy_user_id } = accessRecord;

      let result;
      if (isEditMode) {
        result = await updateProMaxModel(formData.model_id, modelData);
      } else {
        result = await createProMaxModel(modelData, hy_user_id);
      }

      if (result.success) {
        alert(`Model ${isEditMode ? 'updated' : 'created'} successfully!`);
        navigate('/admin/promax-models');
      } else {
        throw new Error(result.error || 'Failed to save model');
      }
    } catch (err) {
      console.error('Error saving model:', err);
      setError(err instanceof Error ? err.message : 'Failed to save model');
    } finally {
      setIsSaving(false);
    }
  };

  if (isEditMode && loadingModel) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner />
          <p className="text-th-secondary mt-4">Loading model...</p>
        </div>
      </div>
    );
  }

  if (isEditMode && loadModelError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md bg-elevated border border-subtle rounded-lg p-8">
          <p className="text-status-error mb-4">Error loading model: {loadModelError}</p>
          <button onClick={goBack} className="button-primary">
            Back to Models
          </button>
        </div>
      </div>
    );
  }

  if (isEditMode && !existingModel && !loadingModel) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md bg-elevated border border-subtle rounded-lg p-8">
          <p className="text-status-error mb-4">Model not found</p>
          <p className="text-th-secondary mb-4">The model with ID "{modelId}" does not exist.</p>
          <button onClick={goBack} className="button-primary">
            Back to Models
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-base">
      <AdminPageToolbar
        maxWidthClass="max-w-5xl"
        title={isEditMode ? 'Edit ProMax Model' : 'Create ProMax Model'}
        subtitle={isEditMode ? 'Update model configuration' : 'Configure a new custom FAL model'}
      >
        <div className="flex flex-wrap items-center gap-2 justify-end">
          <button
            type="button"
            onClick={() => setShowGenerateModal(true)}
            className="button-secondary flex items-center gap-2"
          >
            <Sparkles className="w-4 h-4" />
            Generate
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="button-primary flex items-center gap-2"
          >
            {isSaving ? <LoadingSpinner size="sm" /> : <Save className="w-4 h-4" />}
            {isSaving ? 'Saving...' : 'Save Model'}
          </button>
        </div>
      </AdminPageToolbar>

      <div className="min-h-0 flex-1 overflow-y-auto max-w-5xl mx-auto w-full px-4 sm:px-6 pb-20 pt-2">
        {error && (
          <div className="mb-6 p-4 bg-status-error/10 border border-status-error/20 rounded-lg">
            <p className="text-status-error">{error}</p>
          </div>
        )}

        <div className="space-y-6">
          <div className="bg-elevated border border-subtle rounded-lg p-6">
            <h2 className="text-lg font-semibold text-th-primary mb-4">Basic Information</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-th-primary mb-2">
                  Model ID *
                </label>
                <input
                  type="text"
                  value={formData.model_id}
                  onChange={(e) => setFormData({ ...formData, model_id: e.target.value })}
                  disabled={isEditMode}
                  className="input-field w-full"
                  placeholder="e.g., flux-pro-ultra"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-th-primary mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-field w-full"
                  placeholder="e.g., FLUX Pro Ultra"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-th-primary mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input-field w-full"
                  rows={3}
                  placeholder="Describe what this model does..."
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-th-primary mb-2">
                  FAL Endpoint *
                </label>
                <input
                  type="text"
                  value={formData.fal_endpoint}
                  onChange={(e) => setFormData({ ...formData, fal_endpoint: e.target.value })}
                  className="input-field w-full"
                  placeholder="e.g., fal-ai/flux-pro/v1.1-ultra"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-th-primary mb-2">
                  Hyper App ID *
                </label>
                <input
                  type="text"
                  value={formData.app_id}
                  onChange={(e) => setFormData({ ...formData, app_id: e.target.value })}
                  className="input-field w-full"
                  placeholder="From Hyper developer console — used for /promax/… session validation"
                />
                <p className="text-xs text-th-secondary mt-1">
                  Same pattern as Page Settings: one App ID per ProMax model for public /promax links.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-elevated border border-subtle rounded-lg p-6">
            <h2 className="text-lg font-semibold text-th-primary mb-4">Categorization</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-th-primary mb-2">
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value as Category })}
                  className="input-field w-full"
                >
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                  <option value="audio">Audio</option>
                  <option value="text">Text</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-th-primary mb-2">
                  Subcategory
                </label>
                <input
                  type="text"
                  value={formData.subcategory}
                  onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                  className="input-field w-full"
                  placeholder="e.g., generation, enhancement"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-th-primary mb-2">
                  Tags (comma-separated)
                </label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  className="input-field w-full"
                  placeholder="e.g., flux, ultra-hd, fast"
                />
              </div>
            </div>
          </div>

          <div className="bg-elevated border border-subtle rounded-lg p-6">
            <h2 className="text-lg font-semibold text-th-primary mb-4">Configuration</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-th-primary mb-2">
                  Credit Cost
                </label>
                <input
                  type="number"
                  value={formData.credit_cost}
                  onChange={(e) => setFormData({ ...formData, credit_cost: parseInt(e.target.value) })}
                  className="input-field w-full"
                  min="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-th-primary mb-2">
                  Estimated Time
                </label>
                <input
                  type="text"
                  value={formData.estimated_time}
                  onChange={(e) => setFormData({ ...formData, estimated_time: e.target.value })}
                  className="input-field w-full"
                  placeholder="e.g., 10-15 seconds"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-th-primary mb-2">
                  Output Type
                </label>
                <select
                  value={formData.output_type}
                  onChange={(e) => setFormData({ ...formData, output_type: e.target.value as OutputType })}
                  className="input-field w-full"
                >
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                  <option value="audio">Audio</option>
                  <option value="text">Text</option>
                  <option value="json">JSON</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-th-primary mb-2">
                  Sort Order
                </label>
                <input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) })}
                  className="input-field w-full"
                />
              </div>
            </div>
          </div>

          <div className="bg-elevated border border-subtle rounded-lg p-6">
            <h2 className="text-lg font-semibold text-th-primary mb-4">Output Mapping</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-th-primary mb-2">
                  Primary Output Key
                </label>
                <input
                  type="text"
                  value={formData.primary_output_key}
                  onChange={(e) => setFormData({ ...formData, primary_output_key: e.target.value })}
                  className="input-field w-full"
                  placeholder="e.g., images"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-th-primary mb-2">
                  Error Key
                </label>
                <input
                  type="text"
                  value={formData.error_key}
                  onChange={(e) => setFormData({ ...formData, error_key: e.target.value })}
                  className="input-field w-full"
                  placeholder="e.g., error"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-th-primary mb-2">
                  Secondary Output Keys (comma-separated)
                </label>
                <input
                  type="text"
                  value={formData.secondary_output_keys}
                  onChange={(e) => setFormData({ ...formData, secondary_output_keys: e.target.value })}
                  className="input-field w-full"
                  placeholder="e.g., video_url, audio_url"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-th-primary mb-2">
                  Metadata Keys (comma-separated)
                </label>
                <input
                  type="text"
                  value={formData.metadata_keys}
                  onChange={(e) => setFormData({ ...formData, metadata_keys: e.target.value })}
                  className="input-field w-full"
                  placeholder="e.g., seed, duration, timings"
                />
              </div>
            </div>
          </div>

          <div className="bg-elevated border border-subtle rounded-lg p-6">
            <h2 className="text-lg font-semibold text-th-primary mb-4">Polling Configuration</h2>
            <div className="space-y-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_long_running}
                  onChange={(e) => setFormData({ ...formData, is_long_running: e.target.checked })}
                  className="checkbox"
                />
                <span className="text-th-primary">Long-running task (requires polling)</span>
              </label>
              {formData.is_long_running && (
                <div className="grid grid-cols-2 gap-4 pl-6">
                  <div>
                    <label className="block text-sm font-medium text-th-primary mb-2">
                      Stream Node ID
                    </label>
                    <input
                      type="text"
                      value={formData.fal_stream_node_id}
                      onChange={(e) => setFormData({ ...formData, fal_stream_node_id: e.target.value })}
                      className="input-field w-full"
                      placeholder="e.g., node_123abc"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-th-primary mb-2">
                      Polling Node Name
                    </label>
                    <input
                      type="text"
                      value={formData.fal_polling_node_name}
                      onChange={(e) => setFormData({ ...formData, fal_polling_node_name: e.target.value })}
                      className="input-field w-full"
                      placeholder="e.g., status"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-th-primary mb-2">
                      Polling Interval (seconds)
                    </label>
                    <input
                      type="number"
                      value={formData.polling_interval_seconds}
                      onChange={(e) => setFormData({ ...formData, polling_interval_seconds: parseInt(e.target.value) })}
                      className="input-field w-full"
                      min="1"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-elevated border border-subtle rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-th-primary">Input Fields</h2>
              <button onClick={handleAddField} className="button-secondary flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Add Field
              </button>
            </div>
            {inputFields.length === 0 ? (
              <p className="text-th-secondary text-center py-4">No input fields yet. Add your first field!</p>
            ) : (
              <div className="space-y-4">
                {inputFields.map((field, index) => (
                  <div key={field.id} className="border border-subtle rounded-lg p-4 relative">
                    <button
                      onClick={() => handleRemoveField(index)}
                      className="absolute top-2 right-2 p-1 hover:bg-status-error/20 rounded text-status-error"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <div className="space-y-3">
                      {/* Basic Settings */}
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-th-secondary mb-1">Field Name</label>
                          <input
                            type="text"
                            value={field.name}
                            onChange={(e) => handleFieldChange(index, { name: e.target.value })}
                            className="input-field w-full text-sm"
                            placeholder="prompt"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-th-secondary mb-1">Label</label>
                          <input
                            type="text"
                            value={field.label}
                            onChange={(e) => handleFieldChange(index, { label: e.target.value })}
                            className="input-field w-full text-sm"
                            placeholder="Enter Prompt"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-th-secondary mb-1">Type</label>
                          <select
                            value={field.type}
                            onChange={(e) => handleFieldChange(index, { type: e.target.value as FieldType })}
                            className="input-field w-full text-sm"
                          >
                            <option value="text">Text</option>
                            <option value="textarea">Textarea</option>
                            <option value="number">Number</option>
                            <option value="select">Select</option>
                            <option value="boolean">Boolean</option>
                            <option value="range">Range</option>
                            <option value="file">File</option>
                            <option value="dropdown_with_image">Dropdown with Image</option>
                            <option value="prompt_builder">Prompt Builder</option>
                          </select>
                        </div>
                      </div>

                      {/* Common Settings */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-th-secondary mb-1">Placeholder</label>
                          <input
                            type="text"
                            value={field.placeholder || ''}
                            onChange={(e) => handleFieldChange(index, { placeholder: e.target.value })}
                            className="input-field w-full text-sm"
                            placeholder="Enter placeholder text"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-th-secondary mb-1">Default Value</label>
                          <input
                            type="text"
                            value={field.defaultValue || ''}
                            onChange={(e) => handleFieldChange(index, { defaultValue: e.target.value })}
                            className="input-field w-full text-sm"
                            placeholder="Default value"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-th-secondary mb-1">Help Text</label>
                        <input
                          type="text"
                          value={field.helpText || ''}
                          onChange={(e) => handleFieldChange(index, { helpText: e.target.value })}
                          className="input-field w-full text-sm"
                          placeholder="Additional help or description"
                        />
                      </div>

                      {/* Type-specific Settings */}
                      {(field.type === 'number' || field.type === 'range') && (
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-th-secondary mb-1">Min</label>
                            <input
                              type="number"
                              value={field.min || ''}
                              onChange={(e) => handleFieldChange(index, { min: parseFloat(e.target.value) })}
                              className="input-field w-full text-sm"
                              placeholder="Min value"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-th-secondary mb-1">Max</label>
                            <input
                              type="number"
                              value={field.max || ''}
                              onChange={(e) => handleFieldChange(index, { max: parseFloat(e.target.value) })}
                              className="input-field w-full text-sm"
                              placeholder="Max value"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-th-secondary mb-1">Step</label>
                            <input
                              type="number"
                              value={field.step || ''}
                              onChange={(e) => handleFieldChange(index, { step: parseFloat(e.target.value) })}
                              className="input-field w-full text-sm"
                              placeholder="Step"
                            />
                          </div>
                        </div>
                      )}

                      {(field.type === 'select' || field.type === 'dropdown_with_image') && (
                        <div>
                          <label className="block text-xs font-medium text-th-secondary mb-1">
                            Options (JSON array: [{`{"label": "Option 1", "value": "opt1"}`}])
                          </label>
                          <textarea
                            value={field.options ? JSON.stringify(field.options) : ''}
                            onChange={(e) => {
                              try {
                                const parsed = JSON.parse(e.target.value);
                                handleFieldChange(index, { options: parsed });
                              } catch (err) {
                                // Invalid JSON, just update the raw value
                              }
                            }}
                            className="input-field w-full text-sm font-mono"
                            rows={3}
                            placeholder='[{"label": "Option 1", "value": "opt1"}, {"label": "Option 2", "value": "opt2"}]'
                          />
                        </div>
                      )}

                      {field.type === 'prompt_builder' && (
                        <div className="space-y-2">
                          <div>
                            <label className="block text-xs font-medium text-th-secondary mb-1">Base Prompt</label>
                            <textarea
                              value={field.promptBuilder?.basePrompt || ''}
                              onChange={(e) => handleFieldChange(index, {
                                promptBuilder: { ...field.promptBuilder, basePrompt: e.target.value }
                              })}
                              className="input-field w-full text-sm"
                              rows={2}
                              placeholder="Base prompt template"
                            />
                          </div>
                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={field.promptBuilder?.enhancePrompt || false}
                              onChange={(e) => handleFieldChange(index, {
                                promptBuilder: { ...field.promptBuilder, enhancePrompt: e.target.checked }
                              })}
                              className="checkbox"
                            />
                            <span className="text-th-secondary">Enhance Prompt with AI</span>
                          </label>
                        </div>
                      )}

                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={field.required}
                            onChange={(e) => handleFieldChange(index, { required: e.target.checked })}
                            className="checkbox"
                          />
                          <span className="text-th-secondary">Required</span>
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-elevated border border-subtle rounded-lg p-6">
            <h2 className="text-lg font-semibold text-th-primary mb-4">Status</h2>
            <div className="space-y-3">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="checkbox"
                />
                <span className="text-th-primary">Active</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_featured}
                  onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                  className="checkbox"
                />
                <span className="text-th-primary">Featured</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.coming_soon}
                  onChange={(e) => setFormData({ ...formData, coming_soon: e.target.checked })}
                  className="checkbox"
                />
                <span className="text-th-primary">Coming Soon</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {showGenerateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-modal-content">
          <div className="bg-elevated border border-subtle rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-th-primary">Generate ProMax Model</h2>
                <p className="text-th-secondary text-sm">Paste FAL API documentation to auto-generate configuration</p>
              </div>
              <button
                onClick={() => {
                  setShowGenerateModal(false);
                  setApiDocumentation('');
                  setGenerateError(null);
                }}
                className="p-2 hover:bg-elevated rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-th-primary" />
              </button>
            </div>

            {generateError && (
              <div className="mb-4 p-4 bg-status-error/10 border border-status-error/20 rounded-lg">
                <p className="text-status-error">{generateError}</p>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-th-primary mb-2">
                API Documentation
              </label>
              <textarea
                value={apiDocumentation}
                onChange={(e) => setApiDocumentation(e.target.value)}
                className="input-field w-full"
                rows={20}
                placeholder="Paste FAL API documentation here (e.g., from https://fal.ai/models/...)&#10;&#10;Example:&#10;# Model Name&#10;Endpoint: fal-ai/model-name&#10;Model ID: fal-ai/model-name&#10;&#10;Input Schema:&#10;- prompt (string, required): The prompt for generation&#10;- num_images (integer, optional): Number of images..."
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowGenerateModal(false);
                  setApiDocumentation('');
                  setGenerateError(null);
                }}
                className="button-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateModel}
                disabled={isGenerating || !apiDocumentation.trim()}
                className="button-primary flex items-center gap-2"
              >
                {isGenerating ? <LoadingSpinner size="sm" /> : <Sparkles className="w-4 h-4" />}
                {isGenerating ? 'Generating...' : 'Generate Model'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
